import BookingItemLayout from '@/routes/account/layout/BookingItemLayout';
import TicketTripsSummary from '@/routes/account/tickets/TicketTripsSummary';
import type { BookingSearchDTO } from '@/types/dto/agent';
import { isBookingSearchItemRefunded } from '@/utils/booking';
import { breakpoints } from '@/utils/breakpoints';
import { format, stripTimeZoneOffset } from '@/utils/dateTime';
import { Divider } from '@packages/webshop-design-system';
import _partition from 'lodash/partition';
import type { FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface TicketSummaryProps {
  bookingSearchItem: BookingSearchDTO;
  isActive: boolean;
}

const TicketSummary: FC<TicketSummaryProps> = ({
  bookingSearchItem,
  isActive,
}) => {
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });

  const startTime =
    bookingSearchItem.journeyOrigin?.time &&
    format(
      stripTimeZoneOffset(bookingSearchItem.journeyOrigin.time),
      'EEE, dd MMM yyyy'
    );
  const endTime =
    bookingSearchItem.journeyDestination?.time &&
    format(
      stripTimeZoneOffset(bookingSearchItem.journeyDestination.time),
      'EEE, dd MMM yyyy'
    );
  const dateRange =
    startTime && endTime && startTime !== endTime
      ? `${startTime} - ${endTime}`
      : (startTime ?? endTime);

  // Only surface trips that still have active admissions — e.g. drop legs
  // whose admissions were exchanged/refunded away so they don't show as
  // live tickets in the preview.
  const activeTripSummaries = (bookingSearchItem.tripSummaries ?? []).filter(
    (trip) => trip.hasActiveAdmissions
  );

  const [outboundTripsSummaries, inboundTripsSummaries] = _partition(
    activeTripSummaries,
    (trip) => trip.direction === 'OUTBOUND'
  );

  const isRefunded = isBookingSearchItemRefunded(bookingSearchItem);

  return (
    <BookingItemLayout
      bookingSearchItem={bookingSearchItem}
      isActive={isActive}
      dateRange={dateRange}
    >
      {Boolean(outboundTripsSummaries.length) && (
        <TicketTripsSummary
          title="outboundTrips"
          tripSummaries={outboundTripsSummaries}
          isRefunded={isRefunded}
        />
      )}

      {Boolean(
        outboundTripsSummaries.length && inboundTripsSummaries.length
      ) && (
        <>
          <Divider
            orientation={isLaptopOrBigger ? 'vertical' : 'horizontal'}
            className="border-neutral-light"
            grow={false}
          />
          <TicketTripsSummary
            title="returnTrips"
            tripSummaries={inboundTripsSummaries}
            isRefunded={isRefunded}
          />
        </>
      )}
    </BookingItemLayout>
  );
};

export default TicketSummary;
